/* eslint-disable react/prop-types */
import { RadioSelect, SecurityPosture, SelectDropDown, Skeleton, Toggle } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import { isEmpty } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getCustomers, getSelectedCustomer } from '../../../../../_augmentt/selectors/customers'
import * as secure from '../../../../../_augmentt/ducks/secure'
import { getUserCustomers } from '../../../../crud/orgs.crud'
import { getSecureCheckSettings, updateSecureCheckSettings } from '../../../../crud/secure.crud'
import { INTEGRATION_APPS } from '../../utils/integrations'
import CompanySettingSelector from './CompanySettingSelector'
import styles from './CustomizeAuditModal.module.scss'
import { secureApi, useLazyGetSecurityPostureQuery } from '../../../../../_augmentt/services/secure'

const { getPostureName, sortSecurityChecks } = SecurityPosture.utils

const getUpdatedSettings = ({ settings, originalSettingsMap }) => {
  // verify which settings have changed - only send what customer has modified
  const updatedSettings = []

  settings.forEach((setting) => {
    if (
      setting.enabled !== originalSettingsMap[setting.module].enabled ||
      setting.inherit !== originalSettingsMap[setting.module].inherit
    ) {
      updatedSettings.push({
        ...setting,
        customer: setting.customer ? Number(setting.customer) : null, // guarantee that we are sending customer id as number
        inherit: undefined, // means nothing to the backend so remove this flag before sending it
        enabled: setting.inherit ? null : setting.enabled, // if setting is being inherited mark enabled as null, so backend can delete it
      })
    }
  })

  return updatedSettings
}

function CustomizeAuditModal({ show, onHide }) {
  const intl = useIntl()
  const [submitLoading, setSubmitLoading] = useState(false)
  const [settingLevel, setSettingLevel] = useState('global')
  const [hasGlobalEditAccess, setHasGlobalEditAccess] = useState(false)
  const [dataLoading, setDataLoading] = useState(false)

  const [selectedCompany, setSelectedCompany] = useState(null)
  const [errorStatus, setErrorStatus] = useState(null)
  const [orgSettingsMap, setOrgSettingsMap] = useState({}) // map module to org setting. Org settings for filling up options when customer setting is inheriting
  const [settings, setSettings] = useState([]) // settings worked on the form (org or customer depending on the selection)
  const [originalSettingsMap, setOriginalSettingsMap] = useState([]) // map module to the original setting, used to check what has changed when saving
  const [companyList, setCompanyList] = useState([])
  const [settingsLevelOptions, setSettingsLevelOptions] = useState([])
  const [checkSearch, setCheckSearch] = useState('')

  const updatedSettings = useMemo(
    () => getUpdatedSettings({ settings, originalSettingsMap }),
    [originalSettingsMap, settings],
  )

  const dispatch = useDispatch()
  const history = useHistory()

  const selectedCustomer = useSelector(getSelectedCustomer)
  const allCustomers = useSelector(getCustomers)
  const { customers: allowedCustomerIds = [] } = useSelector(({ userPermissions }) => userPermissions.data)
  const [trigger] = useLazyGetSecurityPostureQuery()

  // Retrieves the data for security posture
  const getSecurityPostureData = async (customerId) => {
    try {
      if (customerId !== '*') {
        const securityPostureData = await trigger(customerId, true)

        return securityPostureData
      }
      return []
    } catch (e) {
      console.error(e)
      return e
    }
  }

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define
    getCompanyList().then((data) => {
      const {
        // eslint-disable-next-line no-shadow
        hasGlobalEditAccess,
        toBeUpdatedSettingLevel,
        toBeFetchedCustomerId,
        toBeSelectedCompany,
        // eslint-disable-next-line no-use-before-define
      } = getInitialConfigurations(data)
      setHasGlobalEditAccess(hasGlobalEditAccess)

      setSettingLevel(toBeUpdatedSettingLevel)
      setSelectedCompany(toBeSelectedCompany)
      // eslint-disable-next-line no-use-before-define
      getSettingsData(toBeFetchedCustomerId)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const settingsLevels = [
      {
        label: intl.formatMessage({
          id: 'SECURE.CUSTOMIZE_AUDIT.GLOBAL',
        }),
        value: 'global',
        checked: settingLevel === 'global',
      },
      {
        label: intl.formatMessage({
          id: 'SECURE.CUSTOMIZE_AUDIT.COMPANY',
        }),
        value: 'company',
        checked: settingLevel === 'company',
      },
    ]

    if (!hasGlobalEditAccess) {
      settingsLevels.shift()
    }

    setSettingsLevelOptions(settingsLevels)
    if (settingLevel === 'global' && selectedCompany) {
      setSelectedCompany(null)
      // eslint-disable-next-line no-use-before-define
      getSettingsData('*')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingLevel, hasGlobalEditAccess])

  const hide = () => {
    onHide()
  }

  const getInitialConfigurations = (secureCompanyList) => {
    const hasAccessToAllCustomers = allowedCustomerIds.length === 1 && allowedCustomerIds[0] === 0
    const hasAccessToParentCompany =
      allCustomers?.findIndex((customer) => !customer.parent_id && allowedCustomerIds.includes(customer.id)) >= 0
    // eslint-disable-next-line no-shadow
    const hasGlobalEditAccess = hasAccessToAllCustomers || hasAccessToParentCompany
    const isPreSelectedCustomerIsAllowed =
      secureCompanyList.findIndex((company) => company.value === selectedCustomer?.id) >= 0

    let toBeUpdatedSettingLevel
    let toBeFetchedCustomerId
    let toBeSelectedCompany

    if (selectedCustomer?.id && isPreSelectedCustomerIsAllowed) {
      toBeUpdatedSettingLevel = 'company'
      toBeFetchedCustomerId = selectedCustomer?.id
      toBeSelectedCompany = {
        value: selectedCustomer?.id,
        label: selectedCustomer?.customer_name,
      }
    } else {
      // global
      // eslint-disable-next-line no-lonely-if
      if (hasGlobalEditAccess) {
        toBeUpdatedSettingLevel = 'global'
        toBeFetchedCustomerId = '*'
        toBeSelectedCompany = null
      } else {
        // global but don't have global access then will force to company level
        toBeUpdatedSettingLevel = 'company'
        let firstAllowedCompany = {}
        if (hasAccessToAllCustomers) {
          firstAllowedCompany = { ...secureCompanyList[0] }
        } else {
          const firstAllowedCompanyIndex = secureCompanyList.findIndex((company) =>
            allowedCustomerIds.includes(company.value),
          )
          if (firstAllowedCompanyIndex >= 0) {
            firstAllowedCompany = secureCompanyList[firstAllowedCompanyIndex]
          }
        }
        toBeFetchedCustomerId = firstAllowedCompany.value
        toBeSelectedCompany = firstAllowedCompany
      }
    }

    return {
      hasAccessToAllCustomers,
      hasAccessToParentCompany,
      hasGlobalEditAccess,
      toBeUpdatedSettingLevel,
      toBeFetchedCustomerId,
      toBeSelectedCompany,
    }
  }

  // eslint-disable-next-line consistent-return
  const getCompanyList = async () => {
    setDataLoading(true)
    try {
      let companies = (await getUserCustomers(true))?.data || []

      companies = companies
        .filter((company) => company?.integratedApplications?.includes(INTEGRATION_APPS.OFFICE_365))
        .map(({ id, customer_name: customerName }) => ({
          value: id,
          label: customerName,
        }))

      setCompanyList(companies)
      return companies
    } catch (error) {
      setDataLoading(false)
      setErrorStatus(
        intl.formatMessage({
          id: 'SECURE.CUSTOMIZE_AUDIT.FETCH_ERROR',
        }),
      )
    }
  }

  const buildOrgSettingsMap = async () => {
    try {
      const orgSettingsRespose = await getSecureCheckSettings('*')
      const orgSettingsData = {}

      orgSettingsRespose?.data?.forEach((setting) => {
        orgSettingsData[setting.module] = setting
      })

      setOrgSettingsMap(orgSettingsData)
    } catch (err) {
      console.error('Could not fetch org settings')
    }
  }

  const getSettingsData = async (customerId) => {
    setDataLoading(true)
    try {
      const settingsResponse = await getSecureCheckSettings(customerId)

      const postureSecureChecks = await getSecurityPostureData(customerId)

      let settingsData = settingsResponse?.data

      // sort customer settings based on the order it appears for that customer in the audit (moving the ignored up)
      if (postureSecureChecks?.data?.securityChecks) {
        const allMonitoredIds = sortSecurityChecks(
          postureSecureChecks?.data?.securityChecks?.allMonitored || [],
          intl,
        )?.map((item) => item.checkId)

        // add ignored checks to the top of the list
        const ignoredIds = postureSecureChecks?.data?.securityChecks?.ignored?.map((item) => item.checkId)
        const checkIds = ignoredIds.concat(allMonitoredIds)

        // Sort settings based on the index of their corresponding security check
        settingsData = checkIds.map((checkId) => settingsResponse?.data?.find((setting) => setting.name === checkId))
      } else {
        // for org settings just move the ignored ones up
        const ignoredSettings = []
        const includedSettings = []
        settingsData?.forEach((setting) => {
          if (setting.enabled) {
            includedSettings.push(setting)
          } else {
            ignoredSettings.push(setting)
          }
        })
        settingsData = [...ignoredSettings, ...includedSettings]
      }

      setSettings(settingsData || [])

      // save original settings map
      const originalSettings = (settingsData || []).reduce((acc, setting) => {
        acc[setting.module] = setting
        return acc
      }, {})
      setOriginalSettingsMap(originalSettings)

      // fetch org settings for diplaying inherit values when on customer settings
      if (isEmpty(orgSettingsMap) && customerId !== '*') {
        await buildOrgSettingsMap()
      }

      setDataLoading(false)
    } catch (error) {
      setDataLoading(false)
      setErrorStatus(
        intl.formatMessage({
          id: 'SECURE.CUSTOMIZE_AUDIT.FETCH_ERROR',
        }),
      )
    }
  }

  const enableLoading = () => {
    setSubmitLoading(true)
  }

  const disableLoading = () => {
    setSubmitLoading(false)
  }

  const handleChangeSelectedCompany = async (newCompany) => {
    if (newCompany && newCompany.value !== selectedCompany?.value) {
      await getSettingsData(newCompany.value)
    } else if (hasGlobalEditAccess && !newCompany) {
      await getSettingsData('*')
    }
    setSelectedCompany(newCompany)
  }

  const handleToggleSetting = (updatedSetting) => {
    const newSettings = [...settings]
    const settingIndex = newSettings.findIndex((setting) => setting.module === updatedSetting.module)
    newSettings[settingIndex] = updatedSetting
    setSettings(newSettings)
  }

  const resetToDefaults = () => {
    const newSettings = settings.map((setting) => ({
      ...setting,
      inherit: true,
      enabled: orgSettingsMap[setting.module]?.enabled,
    }))
    setSettings(newSettings)
  }

  const handleSubmit = async () => {
    try {
      enableLoading()

      await updateSecureCheckSettings('*', {
        settings: updatedSettings,
      })

      disableLoading()
      hide()
      history.push('/secure/security-posture')
      dispatch(secure.actions.setShowSecurityChecksPage(undefined))
      dispatch(secureApi.util.resetApiState())
    } catch (error) {
      console.error(error)
      disableLoading()
      setErrorStatus(
        intl.formatMessage({
          id: 'SECURE.CUSTOMIZE_AUDIT.UPDATE_ERROR',
        }),
      )
    }
  }

  const isUpdateDisabled = () => {
    if (settingLevel === 'global' && !submitLoading && !dataLoading) {
      return false
    }
    return submitLoading || dataLoading || (settingLevel === 'company' && !selectedCompany)
  }

  const filteredSettings = settings.filter((setting) => {
    if (!checkSearch) {
      return true
    }

    const formattedName = getPostureName(setting.name, intl)
    return formattedName.toLowerCase().includes(checkSearch.toLowerCase())
  })

  return (
    <Modal
      id="CustomizeAuditModal"
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={show}
      onHide={hide}
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          <FormattedMessage id="SECURE.CUSTOMIZE_AUDIT" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md="12">
            <p>
              <FormattedMessage id="SECURE.CUSTOMIZE_AUDIT_DESCRIPTION" />
            </p>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            {errorStatus && (
              <Alert variant="danger">
                <div className="alert-text">{errorStatus}</div>
              </Alert>
            )}
            <Row>
              <Col md="12">
                <p>
                  <RadioSelect
                    name="settingLevelsGroup"
                    onSelect={(value) => {
                      setSettingLevel(value)
                    }}
                    options={settingsLevelOptions}
                    value={settingLevel}
                  />
                </p>
                {settingLevel === 'company' && (
                  <div className="mx-3">
                    <SelectDropDown
                      defaultLabel={intl.formatMessage({
                        id: 'PLACEHOLDER.SELECT_A_COMPANY',
                      })}
                      onChange={(value) => handleChangeSelectedCompany(value)}
                      defaultValue={selectedCompany}
                      options={companyList}
                    />
                  </div>
                )}
                <hr />
              </Col>
            </Row>

            <div className="d-flex px-3 mb-2">
              <Form.Control
                type="text"
                id="search-security-check"
                placeholder={intl.formatMessage({
                  id: 'SECURE.CUSTOMIZE_AUDIT.SEARCH_CHECK',
                })}
                onChange={(event) => setCheckSearch(event.target.value)}
                value={checkSearch}
                disabled={dataLoading}
              />
              {settingLevel === 'company' && selectedCompany && (
                <Button className="ms-3 text-nowrap" onClick={resetToDefaults} disabled={dataLoading}>
                  <FormattedMessage id="GENERAL.RESET_TO_DEFAULTS" />
                </Button>
              )}
            </div>

            <table className={clsx('text-color-default', styles.settingsTable)}>
              <thead>
                <tr>
                  <th className={clsx('font-bolder text-muted', styles.securityCheckTh)}>
                    <FormattedMessage id="SECURE.CUSTOMIZE_AUDIT.SECURITY_CHECK" />
                  </th>
                  <th className="font-bolder text-muted text-center text-nowrap">
                    <FormattedMessage id="SECURE.CUSTOMIZE_AUDIT.INCLUDE_IN_AUDIT" />
                  </th>
                </tr>
              </thead>

              <tbody>
                {dataLoading && // mock table loading with approximate number of postures that we have
                  Array(100)
                    .fill()
                    .map((num) => (
                      <tr key={num}>
                        <td>
                          <Skeleton />
                        </td>
                        <td>
                          <Skeleton />
                        </td>
                      </tr>
                    ))}

                {!dataLoading &&
                  filteredSettings.map((setting) => (
                    <tr key={setting.module}>
                      <td>{getPostureName(setting.name, intl)}</td>
                      <td className="text-center">
                        {settingLevel === 'company' && selectedCompany ? (
                          <CompanySettingSelector
                            setting={setting}
                            orgSetting={orgSettingsMap[setting.module]}
                            onChange={handleToggleSetting}
                          />
                        ) : (
                          <div className={styles.toggleWrapper}>
                            <Toggle
                              id={`${setting.name}-toggle`}
                              onChange={(checked) =>
                                handleToggleSetting({ ...setting, enabled: checked, inherit: false })
                              }
                              checked={setting.enabled}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {!dataLoading && !filteredSettings.length && (
              <div className="text-center text-muted">
                <FormattedMessage id="GENERAL.NO_DATA" />
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button onClick={hide} variant="light">
          <FormattedMessage id="GENERAL.CANCEL" />
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isUpdateDisabled() || !updatedSettings?.length}
          id="addUserSubmit"
          variant="primary"
          className={`btn-elevate adduser__btn-primary ${clsx({
            'spinner spinner--right spinner--md spinner--light': submitLoading,
          })}`}
        >
          <FormattedMessage id="GENERAL.UPDATE" />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CustomizeAuditModal
